import { DEFAULT_SITE_CONTENT, type PublicBoot, type PublicPostFilter, type SiteContent } from '@hbac/contract';
import { keepPreviousData, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { getApi } from '../api';

/**
 * Reads for the public site.
 *
 * Kept apart from services/queries.ts on purpose: everything in that file is
 * gated on `useAuth`, and these must run with no session at all. Mixing them
 * would make it easy to attach an `enabled: Boolean(token)` that silently
 * blanks the public pages for anonymous visitors.
 */

export type { PublicPostFilter };

export const publicKeys = {
  posts: (f: PublicPostFilter) => ['public', 'posts', f] as const,
  post: (slug: string) => ['public', 'post', slug] as const,
  siteContent: ['public', 'site-content'] as const,
};

/**
 * Landing and HBAAC page copy.
 *
 * `content` is null only while the first load is in flight, so sections can
 * show a skeleton rather than flash the built-in defaults and then swap. If
 * the request fails, the defaults ARE used: a public page must never be blank.
 */
export function usePublicSiteContent(): { content: SiteContent | null; loading: boolean } {
  const q = useQuery({
    queryKey: publicKeys.siteContent,
    queryFn: () => getApi().getSiteContent(),
    staleTime: 60 * 1000,
  });
  if (q.data) return { content: q.data, loading: false };
  if (q.isError) return { content: DEFAULT_SITE_CONTENT, loading: false };
  return { content: null, loading: true };
}

const POSTS_STALE = 5 * 60 * 1000;

export function usePublicPosts(filter: PublicPostFilter = {}) {
  return useQuery({
    queryKey: publicKeys.posts(filter),
    queryFn: () => getApi().listPublishedPosts(filter),
    // Published articles change rarely; this keeps repeat visits instant and
    // keeps bots from hammering D1.
    staleTime: POSTS_STALE,
    // Changing page keeps the current cards on screen until the next ones
    // arrive, instead of dropping back to skeletons.
    placeholderData: keepPreviousData,
  });
}

/** Loads a page of posts into the cache ahead of the click. */
export function usePrefetchPublicPosts() {
  const client = useQueryClient();
  return (filter: PublicPostFilter) =>
    void client.prefetchQuery({
      queryKey: publicKeys.posts(filter),
      queryFn: () => getApi().listPublishedPosts(filter),
      staleTime: POSTS_STALE,
    });
}

export function usePublicPost(slug: string | undefined) {
  return useQuery({
    queryKey: publicKeys.post(slug ?? ''),
    queryFn: () => getApi().getPublishedPost(slug as string),
    enabled: Boolean(slug),
    staleTime: 5 * 60 * 1000,
    // A missing slug is a 404, not a transient failure — retrying just delays
    // showing the not-found page.
    retry: false,
  });
}

/**
 * The data a public page's HTML came with (Plan AE 4.1): put in the cache
 * under the keys the hooks above use, so the page renders without waiting for
 * the API. Read once, at start, then removed.
 */
export function seedPublicBoot(client: QueryClient): void {
  const el = typeof document !== 'undefined' ? document.getElementById('hbac-boot') : null;
  if (!el) return;
  try {
    const boot = JSON.parse(el.textContent ?? '{}') as PublicBoot;
    if (boot.siteContent) client.setQueryData(publicKeys.siteContent, boot.siteContent);
    if (boot.posts) client.setQueryData(publicKeys.posts(boot.posts.filter), boot.posts.data);
    if (boot.post) client.setQueryData(publicKeys.post(boot.post.slug), boot.post.data);
  } catch {
    // A malformed block only means the page asks the API, as it always did.
  } finally {
    el.remove();
  }
}
